import { Fragment, useMemo, useState } from 'react';
import { LineChart } from '../charts/LineChart';
import { ChartCard, Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

/** The invoice split, before tax: the three groups and whatever is left. */
interface Split {
  labor: number;
  recurring: number;
  products: number;
  other: number;
}
interface Invoice extends Split {
  id: number;
  number: string;
  date: string | null;
  due: string | null;
  paid: string | null;
  total: number;
  tax: number;
  overdue: boolean;
}
interface Client extends Split {
  id: number;
  name: string;
  count: number;
  total: number;
  tax: number;
  subtotal: number;
  paid: number;
  outstanding: number;
  overdue: number;
  invoices: Invoice[];
}
interface Payload {
  from: string;
  to: string;
  fetchedUtc: string;
  activeCompanies: number;
  voided: number;
  inactive: { invoices: number; total: number };
  /** False when the Autotask user cannot read BillingItems — no split shown. */
  breakdown: boolean;
  lines: number;
  unmapped: { types: number[]; total: number };
  unallocated: number;
  unreconciled: number;
  clients: Client[];
}

const money = (v: number) => formatNumber(v, 'currency');
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function quick(k: 'month' | 'lastMonth' | 'quarter' | 'lastQuarter' | 'ytd' | 'lastYear' | '12m'): { from: string; to: string } {
  const d = new Date();
  const y = d.getFullYear();
  const mo = d.getMonth();
  const q = Math.floor(mo / 3);
  const ld = (dt: Date) => toLocalDateString(dt);
  switch (k) {
    case 'month':
      return { from: ld(new Date(y, mo, 1)), to: todayLocal() };
    case 'lastMonth':
      return { from: ld(new Date(y, mo - 1, 1)), to: ld(new Date(y, mo, 0)) };
    case 'quarter':
      return { from: ld(new Date(y, q * 3, 1)), to: todayLocal() };
    case 'lastQuarter':
      return { from: ld(new Date(y, (q - 1) * 3, 1)), to: ld(new Date(y, q * 3, 0)) };
    case 'ytd':
      return { from: ld(new Date(y, 0, 1)), to: todayLocal() };
    case 'lastYear':
      return { from: ld(new Date(y - 1, 0, 1)), to: ld(new Date(y - 1, 11, 31)) };
    default:
      return { from: ld(new Date(y, mo - 11, 1)), to: todayLocal() };
  }
}
/** One month of the year: every figure the page shows, summed over the invoices dated in it. */
/* A flat type, not an interface (nor one extending Split): the chart takes
   Record<string, unknown> rows, which an interface never satisfies. */
type Month = {
  labor: number;
  recurring: number;
  products: number;
  other: number;
  month: string;
  count: number;
  total: number;
  subtotal: number;
  tax: number;
  paid: number;
  outstanding: number;
  overdue: number;
};

/** January to the current month, each invoice in the month of its invoice date. */
function byMonth(from: string, to: string, clients: Client[]): Month[] {
  const rows = new Map<string, Month>();
  for (let m = from.slice(0, 7); m <= to.slice(0, 7); ) {
    rows.set(m, { month: m, count: 0, total: 0, subtotal: 0, tax: 0, paid: 0, outstanding: 0, overdue: 0, labor: 0, recurring: 0, products: 0, other: 0 });
    const [y, mo] = m.split('-').map(Number) as [number, number];
    m = mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
  }
  for (const c of clients)
    for (const i of c.invoices) {
      const r = i.date ? rows.get(i.date.slice(0, 7)) : undefined;
      if (!r) continue;
      r.count += 1;
      r.total += i.total;
      r.tax += i.tax;
      r.subtotal += i.total - i.tax;
      if (i.paid) r.paid += i.total;
      else r.outstanding += i.total;
      if (i.overdue) r.overdue += i.total;
      r.labor += i.labor;
      r.recurring += i.recurring;
      r.products += i.products;
      r.other += i.other;
    }
  return [...rows.values()];
}

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString([], { month: 'short' });
const moneyShort = (v: number) =>
  Math.abs(v) >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`;
const moneyCol = (key: keyof Month & string, label: string) => ({ key, label, align: 'right' as const, render: (r: Month) => money(r[key] as number) });

/**
 * This year, month by month — every figure on the page as a line. Dollars and
 * the invoice count never share an axis, and the line split sits apart from the
 * totals so no chart carries more than five lines.
 */
function YearCharts({ months, split, loading, error, filtered }: { months: Month[]; split: boolean; loading: boolean; error: string | null; filtered: boolean }) {
  const yearNo = months[0]?.month.slice(0, 4) ?? '';
  const who = filtered ? 'the clients chosen above' : 'every active client';
  if (error) return <ErrorBanner message={error} />;
  if (loading) return <Loading label={`Reading ${yearNo} invoices from Autotask`} />;
  return (
    <div className="admin-stack mb-4">
      <ChartCard
        title={`${yearNo} by month`}
        subtitle={`Invoiced, before tax, paid, outstanding and overdue for ${who}, each invoice in the month it is dated. The date filter above does not apply here.`}
        table={{
          columns: [
            { key: 'month', label: 'Month', render: (r: Month) => monthLabel(r.month) },
            { key: 'count', label: 'Invoices', align: 'right', render: (r: Month) => num(r.count) },
            moneyCol('total', 'Invoiced'),
            moneyCol('subtotal', 'Before tax'),
            moneyCol('tax', 'Tax'),
            moneyCol('paid', 'Paid'),
            moneyCol('outstanding', 'Outstanding'),
            moneyCol('overdue', 'Overdue'),
            ...(split ? [moneyCol('labor', 'Labor'), moneyCol('recurring', 'Recurring'), moneyCol('products', 'Products'), moneyCol('other', 'Other')] : []),
          ],
          rows: months,
          rowKey: (r) => r.month,
        }}
      >
        <LineChart
          data={months}
          xKey="month"
          series={[
            { key: 'total', label: 'Invoiced', color: 'var(--series-1)' },
            { key: 'subtotal', label: 'Before tax', color: 'var(--series-2)' },
            { key: 'paid', label: 'Paid', color: 'var(--series-3)' },
            { key: 'outstanding', label: 'Outstanding', color: 'var(--series-4)' },
            { key: 'overdue', label: 'Overdue', color: 'var(--series-5)' },
          ]}
          xFormat={monthLabel}
          yFormat={moneyShort}
          zeroBased
          height={280}
        />
      </ChartCard>
      <div className="admin-split">
        {split && (
          <ChartCard title="What the lines bill" subtitle="Labor, recurring service, products and other, before tax, from the invoices' own lines.">
            <LineChart
              data={months}
              xKey="month"
              series={[
                { key: 'labor', label: 'Labor', color: 'var(--series-1)' },
                { key: 'recurring', label: 'Recurring service', color: 'var(--series-2)' },
                { key: 'products', label: 'Products & expenses', color: 'var(--series-3)' },
                { key: 'other', label: 'Other', color: 'var(--series-4)' },
              ]}
              xFormat={monthLabel}
              yFormat={moneyShort}
              zeroBased
              height={240}
            />
          </ChartCard>
        )}
        <ChartCard title="Invoices and tax" subtitle="How many invoices went out each month, and the tax on them.">
          <p className="xs muted">Invoices</p>
          <LineChart data={months} xKey="month" series={[{ key: 'count', label: 'Invoices', color: 'var(--series-2)' }]} xFormat={monthLabel} yFormat={(v) => num(v)} zeroBased area height={140} />
          <p className="xs muted mt-3">Tax</p>
          <LineChart data={months} xKey="month" series={[{ key: 'tax', label: 'Tax', color: 'var(--series-1)' }]} xFormat={monthLabel} yFormat={moneyShort} zeroBased area height={140} />
        </ChartCard>
      </div>
    </div>
  );
}

const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'quarter', label: 'This quarter' },
  { k: 'lastQuarter', label: 'Last quarter' },
  { k: 'ytd', label: 'This year' },
  { k: 'lastYear', label: 'Last year' },
  { k: '12m', label: 'Last 12 months' },
];

/**
 * Reports › Invoice Totals — what every active Autotask client was invoiced
 * in the dates chosen (routes/reportsInvoices.ts): invoices, total before
 * tax, tax, total, paid, outstanding and overdue, with each client's
 * invoices a click away. Filter by client and by date.
 */
export function InvoiceTotalsReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('month'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [client, setClient] = useState('');
  const [search, setSearch] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  /* Its own sort: the app-wide heading sort would split a client from its invoice lines. */
  const [sortBy, setSortBy] = useState<'total' | 'name' | 'outstanding' | 'overdue' | 'count' | 'labor' | 'recurring' | 'products'>('total');
  const [open, setOpen] = useState<Set<number>>(new Set());
  const data = useHeldResource<Payload>(`/reports/invoices?${new URLSearchParams({ from, to })}`);
  const d = data.data;
  /* The year's trend reads its own January-to-today payload, whatever dates the table shows. */
  const yearRange = quick('ytd');
  const year = useHeldResource<Payload>(`/reports/invoices?${new URLSearchParams(yearRange)}`);

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.clients ?? [])
      .filter((c) => (!client || String(c.id) === client) && (!q || c.name.toLowerCase().includes(q)) && (!hideEmpty || client || c.count > 0))
      .sort((a, b) => (sortBy === 'name' ? a.name.localeCompare(b.name) : b[sortBy] - a[sortBy] || a.name.localeCompare(b.name)));
  }, [d, client, search, hideEmpty, sortBy]);

  /* The same client and search filters as the table, over the year's invoices. */
  const months = useMemo(() => {
    const q = search.trim().toLowerCase();
    const picked = (year.data?.clients ?? []).filter((c) => (!client || String(c.id) === client) && (!q || c.name.toLowerCase().includes(q)));
    return byMonth(yearRange.from, yearRange.to, picked);
  }, [year.data, client, search, yearRange.from, yearRange.to]);

  const sum = (k: 'count' | 'total' | 'tax' | 'subtotal' | 'paid' | 'outstanding' | 'overdue' | keyof Split) => shown.reduce((t, c) => t + c[k], 0);
  /* The breakdown columns stand down when Autotask gave us no invoice lines. */
  const split = d?.breakdown ?? false;
  const cols = split ? 12 : 8;
  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const exportCsv = () =>
    downloadCsv(`invoice_totals_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Client', 'Invoices', ...(split ? ['Labor', 'Recurring', 'Products', 'Other'] : []), 'Before tax', 'Tax', 'Total', 'Paid', 'Outstanding', 'Overdue'],
      ...shown.map((c) => [
        c.name,
        c.count,
        ...(split ? [c.labor, c.recurring, c.products, c.other] : []),
        c.subtotal,
        c.tax,
        c.total,
        c.paid,
        c.outstanding,
        c.overdue,
      ]),
      [],
      ['Client', 'Invoice', 'Date', 'Due', 'Paid', ...(split ? ['Labor', 'Recurring', 'Products', 'Other'] : []), 'Before tax', 'Tax', 'Total'],
      ...shown.flatMap((c) =>
        c.invoices.map((i) => [
          c.name,
          i.number,
          i.date,
          i.due,
          i.paid ?? '',
          ...(split ? [i.labor, i.recurring, i.products, i.other] : []),
          i.total - i.tax,
          i.tax,
          i.total,
        ]),
      ),
    ]);

  return (
    <>
      <PageHead
        title="Invoice Totals"
        lede="What every active client was invoiced in the dates you choose — split into labor, recurring service and products, then tax, total, paid and outstanding — read live from Autotask. Click a client for its invoices."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Invoice date from">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((q) => (
                <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(q.k))}>
                  {q.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-3" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Client">
              <select className="select" value={client} onChange={(e) => setClient(e.target.value)} style={{ minWidth: 260 }}>
                <option value="">All active clients</option>
                {(d?.clients ?? [])
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client name" />
            </Field>
            <Field label="Sort by">
              <select className="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
                <option value="total">Total, highest first</option>
                <option value="name">Client name, A–Z</option>
                <option value="outstanding">Outstanding, highest first</option>
                <option value="overdue">Overdue, highest first</option>
                <option value="count">Number of invoices</option>
                {split && <option value="labor">Labor, highest first</option>}
                {split && <option value="recurring">Recurring, highest first</option>}
                {split && <option value="products">Products, highest first</option>}
              </select>
            </Field>
            <label className="checkbox">
              <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
              Hide clients with no invoices
            </label>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!shown.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            Autotask invoices dated {day(from)} – {day(to)} (by invoice date), voided invoices left out. Totals include tax; &ldquo;paid&rdquo; is an invoice
            with a paid date in Autotask.
            {d && ` ${num(d.activeCompanies)} active clients.`}
            {d && d.voided > 0 && ` ${num(d.voided)} voided invoices not counted.`}
            {d && d.inactive.invoices > 0 && ` ${num(d.inactive.invoices)} invoices (${money(d.inactive.total)}) belong to inactive companies and are not listed.`}
          </p>
          {d && split && (
            <p className="xs muted mt-1">
              Labor, recurring and products come from the invoice&rsquo;s own lines in Autotask ({num(d.lines)} billing items), before tax. They are what the
              lines report rather than a split of the total, so they need not add up to &ldquo;before tax&rdquo;: Autotask records labor at the value of the
              work even where a contract covers it and the invoice charges nothing.
              {d.unmapped.total !== 0 &&
                ` ${money(d.unmapped.total)} sits on billing item ${d.unmapped.types.length === 1 ? 'type' : 'types'} ${d.unmapped.types.join(', ')}, which this report does not group, and is counted as “other”.`}
              {d.unreconciled > 0 &&
                ` ${num(d.unreconciled)} ${d.unreconciled === 1 ? 'invoice does' : 'invoices do'} not match their own lines, by ${money(d.unallocated)} in total — left out of the groups rather than added to one.`}
            </p>
          )}
          {d && !split && d.clients.some((c) => c.count > 0) && (
            <p className="xs muted mt-1">
              No invoice lines came back from Autotask, so the labor / recurring / products split is not shown. The API user needs to be able to read billing
              items.
            </p>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading invoices from Autotask" />}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Invoiced" value={money(sum('total'))} foot={`${num(sum('count'))} invoices · ${money(sum('tax'))} tax`} />
            <Stat label="Before tax" value={money(sum('subtotal'))} foot={`${num(shown.filter((c) => c.count > 0).length)} clients invoiced`} />
            <Stat label="Paid" value={money(sum('paid'))} foot="invoices with a paid date" />
            <Stat label="Outstanding" value={money(sum('outstanding'))} foot={`${money(sum('overdue'))} past due`} />
          </div>

          {split && (
            <div className="grid grid-4 mb-4">
              {(
                [
                  ['labor', 'Labor'],
                  ['recurring', 'Recurring service'],
                  ['products', 'Products & expenses'],
                  ['other', 'Other'],
                ] as const
              ).map(([k, label]) => {
                const v = sum(k);
                /* Against the lines, not the subtotal — the groups are not a split of it. */
                const of = sum('labor') + sum('recurring') + sum('products') + sum('other');
                return <Stat key={k} label={label} value={money(v)} foot={of > 0 ? `${Math.round((v / of) * 100)}% of what the lines bill` : 'before tax'} />;
              })}
            </div>
          )}

          <YearCharts months={months} split={year.data?.breakdown ?? split} loading={year.loading} error={year.error} filtered={!!client || !!search.trim()} />

          <section className="card">
            <div className="table-wrap">
              <table className="table inv-table" data-nosort>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="num">Invoices</th>
                    {split && <th className="num">Labor</th>}
                    {split && <th className="num">Recurring</th>}
                    {split && <th className="num">Products</th>}
                    {split && <th className="num">Other</th>}
                    <th className="num">Before tax</th>
                    <th className="num">Tax</th>
                    <th className="num">Total</th>
                    <th className="num">Paid</th>
                    <th className="num">Outstanding</th>
                    <th className="num">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={cols} className="muted small">
                        No invoices for {client ? 'this client' : 'any client'} in these dates.
                      </td>
                    </tr>
                  )}
                  {shown.map((c) => {
                    const isOpen = open.has(c.id);
                    return (
                      <Fragment key={c.id}>
                        <tr className={isOpen ? 'is-open' : undefined}>
                          <td>
                            {c.count > 0 ? (
                              <button type="button" className="link-button" onClick={() => toggle(c.id)} aria-expanded={isOpen}>
                                {isOpen ? '▾' : '▸'} {c.name}
                              </button>
                            ) : (
                              <span className="muted">{c.name}</span>
                            )}
                          </td>
                          <td className="num">{num(c.count)}</td>
                          {split && <td className="num">{money(c.labor)}</td>}
                          {split && <td className="num">{money(c.recurring)}</td>}
                          {split && <td className="num">{money(c.products)}</td>}
                          {split && <td className="num">{money(c.other)}</td>}
                          <td className="num">{money(c.subtotal)}</td>
                          <td className="num">{money(c.tax)}</td>
                          <td className="num strong">{money(c.total)}</td>
                          <td className="num">{money(c.paid)}</td>
                          <td className="num">{money(c.outstanding)}</td>
                          <td className={`num${c.overdue > 0 ? ' inv-overdue' : ''}`}>{money(c.overdue)}</td>
                        </tr>
                        {isOpen &&
                          c.invoices.map((i) => (
                            <tr key={i.id} className="inv-line">
                              <td className="xs">
                                Invoice <span className="mono">{i.number}</span> · {day(i.date)}
                              </td>
                              <td className="xs muted">due {day(i.due)}</td>
                              {split && <td className="num xs">{money(i.labor)}</td>}
                              {split && <td className="num xs">{money(i.recurring)}</td>}
                              {split && <td className="num xs">{money(i.products)}</td>}
                              {split && <td className="num xs">{money(i.other)}</td>}
                              <td className="num xs">{money(i.total - i.tax)}</td>
                              <td className="num xs">{money(i.tax)}</td>
                              <td className="num xs strong">{money(i.total)}</td>
                              <td className="num xs">{i.paid ? `paid ${day(i.paid)}` : '—'}</td>
                              <td className="num xs">{i.paid ? '—' : money(i.total)}</td>
                              <td className={`num xs${i.overdue ? ' inv-overdue' : ''}`}>{i.overdue ? 'overdue' : '—'}</td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                  {shown.length > 1 && (
                    <tr className="cr-total">
                      <td>{num(shown.length)} clients</td>
                      <td className="num">{num(sum('count'))}</td>
                      {split && <td className="num">{money(sum('labor'))}</td>}
                      {split && <td className="num">{money(sum('recurring'))}</td>}
                      {split && <td className="num">{money(sum('products'))}</td>}
                      {split && <td className="num">{money(sum('other'))}</td>}
                      <td className="num">{money(sum('subtotal'))}</td>
                      <td className="num">{money(sum('tax'))}</td>
                      <td className="num">{money(sum('total'))}</td>
                      <td className="num">{money(sum('paid'))}</td>
                      <td className="num">{money(sum('outstanding'))}</td>
                      <td className="num">{money(sum('overdue'))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
